<template>
  <section class="schedule section" :class="{ 'is-english': isEnLang }" id="schedule">
    <div class="layout-content">
      <SubTitle :text="title" />
      <p class="timezone">{{ data.timezone }}</p>

      <div class="schedule-region-tabs" role="tablist" :aria-label="isEnLang ? 'Schedule region' : '赛程区域切换'">
        <button
          type="button"
          role="tab"
          :aria-selected="scheduleRegion === 'domestic'"
          :class="{ active: scheduleRegion === 'domestic' }"
          @click="scheduleRegion = 'domestic'"
        >
          <span>01</span>
          <span class="tab-copy">
            <strong>{{ isEnLang ? 'Domestic Competition' : '国内赛道' }}</strong>
            <small>{{ isEnLang ? 'Artificial Intelligence · Data Analytics' : '人工智能 · 数据分析' }}</small>
          </span>
        </button>
        <button
          type="button"
          role="tab"
          :aria-selected="scheduleRegion === 'international'"
          :class="{ active: scheduleRegion === 'international' }"
          @click="scheduleRegion = 'international'"
        >
          <span>02</span>
          <span class="tab-copy">
            <strong>{{ isEnLang ? 'International Competition' : '国际赛道' }}</strong>
            <small>{{ isEnLang ? 'Artificial Intelligence' : '人工智能' }}</small>
          </span>
        </button>
      </div>

      <transition name="schedule-panel" mode="out-in">
        <div :key="scheduleRegion" class="region-panel" :class="`region-panel--${scheduleRegion}`">
          <div class="region-heading">
            <span>{{ scheduleRegion === 'domestic' ? 'DOMESTIC' : 'INTERNATIONAL' }}</span>
            <h3>{{ regionTitle }}</h3>
            <p>{{ regionDescription }}</p>
          </div>

          <p v-if="scheduleRegion === 'domestic'" class="workshop-notice">
            <span>{{ isEnLang ? '7 SEP — 23 OCT' : '09.07—10.23' }}</span>
            <strong>{{ isEnLang ? '15 workshops will be held progressively' : '陆续开展 15 场工作坊' }}</strong>
          </p>

          <div class="horizontal-timeline" :class="{ 'is-single': filteredTracks.length === 1 }">
            <svg class="horizontal-routes" viewBox="0 0 1200 430" preserveAspectRatio="none" aria-hidden="true">
              <defs>
                <linearGradient id="horizontalCommon" gradientUnits="userSpaceOnUse" x1="0" y1="215" x2="310" y2="215"><stop offset="0" stop-color="#d45da3"/><stop offset="1" stop-color="#8457c7"/></linearGradient>
                <linearGradient id="horizontalAi" gradientUnits="userSpaceOnUse" x1="310" y1="215" x2="1200" y2="135"><stop offset="0" stop-color="#8457c7"/><stop offset="1" stop-color="#3f62dc"/></linearGradient>
                <linearGradient id="horizontalData" gradientUnits="userSpaceOnUse" x1="310" y1="215" x2="1200" y2="295"><stop offset="0" stop-color="#8457c7"/><stop offset="1" stop-color="#e13e72"/></linearGradient>
                <linearGradient id="horizontalInternational" gradientUnits="userSpaceOnUse" x1="310" y1="215" x2="1200" y2="215"><stop offset="0" stop-color="#7359d9"/><stop offset="48%" stop-color="#2788d8"/><stop offset="100%" stop-color="#04a9bd"/></linearGradient>
              </defs>
              <path class="route-common" d="M0 215 H310" />
              <template v-if="scheduleRegion === 'domestic'">
                <path class="route-ai" d="M310 215 C350 215 350 135 400 135 H1030 C1075 135 1075 215 1115 215 H1200" />
                <path class="route-data" d="M310 215 C350 215 350 295 400 295 H1030 C1075 295 1075 215 1115 215" />
              </template>
              <path v-else class="route-international" d="M310 215 H1200" />
            </svg>

            <article v-for="(event, index) in visibleCommonEvents" :key="`wide-common-${event.date}`" class="wide-event wide-common" :class="index % 2 ? 'below' : 'above'" :style="{ left: `${commonPosition(index)}%` }">
              <i></i><div><strong>{{ event.date }}</strong><small>{{ event.title }}</small></div>
            </article>
            <template v-for="(track, trackIndex) in filteredTracks">
              <article v-for="(event, eventIndex) in track.events" :key="`wide-${track.key}-${event.date}`" class="wide-event wide-track" :class="[`lane-${trackIndex}`, `wide-${track.key}`]" :style="{ left: `${trackPosition(eventIndex, track)}%` }">
                <i></i><div><strong>{{ event.date }}</strong><em v-if="track.key !== 'international-ai'">【{{ trackTag(track.key) }}】</em><small>{{ event.title }}</small></div>
              </article>
            </template>
            <article class="wide-event wide-finale">
              <i></i><div><strong>{{ data.finale.date }}</strong><em>{{ isEnLang ? 'FINALE' : '总决赛' }}</em><small>{{ data.finale.title }} · {{ data.finale.place }}</small></div>
            </article>
          </div>

          <div class="vertical-timeline" :class="{ 'is-single': filteredTracks.length === 1 }">
            <svg class="vertical-routes" viewBox="0 0 720 980" preserveAspectRatio="none" aria-hidden="true">
              <defs>
                <linearGradient id="verticalCommon" gradientUnits="userSpaceOnUse" x1="360" y1="0" x2="360" y2="250"><stop offset="0" stop-color="#d45da3"/><stop offset="1" stop-color="#8457c7"/></linearGradient>
                <linearGradient id="verticalAi" gradientUnits="userSpaceOnUse" x1="360" y1="250" x2="245" y2="900"><stop offset="0" stop-color="#8757d5"/><stop offset="42%" stop-color="#5367e1"/><stop offset="1" stop-color="#315fd8"/></linearGradient>
                <linearGradient id="verticalData" gradientUnits="userSpaceOnUse" x1="360" y1="250" x2="475" y2="900"><stop offset="0" stop-color="#9257cf"/><stop offset="42%" stop-color="#db477f"/><stop offset="1" stop-color="#ee315f"/></linearGradient>
                <linearGradient id="verticalInternational" gradientUnits="userSpaceOnUse" x1="360" y1="250" x2="360" y2="900"><stop offset="0" stop-color="#7359d9"/><stop offset="45%" stop-color="#2788d8"/><stop offset="100%" stop-color="#04a9bd"/></linearGradient>
                <linearGradient id="verticalFinal" gradientUnits="userSpaceOnUse" x1="360" y1="900" x2="360" y2="980"><stop offset="0" stop-color="#7b58ca"/><stop offset="1" stop-color="#315fd8"/></linearGradient>
              </defs>
              <path class="route-common" d="M360 0 V250" />
              <template v-if="scheduleRegion === 'domestic'">
                <path class="route-ai" d="M360 250 C360 285 245 285 245 330 V820 C245 860 360 860 360 900" />
                <path class="route-data" d="M360 250 C360 285 475 285 475 330 V820 C475 860 360 860 360 900" />
              </template>
              <path v-else class="route-international" d="M360 250 V900" />
              <path class="route-final" d="M360 900 V980" />
            </svg>

            <article v-for="(event, index) in visibleCommonEvents" :key="`narrow-common-${event.date}`" class="portrait-event portrait-common" :class="index % 2 ? 'copy-right' : 'copy-left'" :style="{ top: `${verticalCommonPosition(index)}px` }">
              <i></i><div><strong>{{ event.date }}</strong><small>{{ event.title }}</small></div>
            </article>

            <template v-for="(track, trackIndex) in filteredTracks">
              <article v-for="(event, eventIndex) in track.events" :key="`narrow-${track.key}-${event.date}`" class="portrait-event portrait-track" :class="[`portrait-lane-${trackIndex}`, trackIndex ? 'copy-right' : 'copy-left']" :style="{ top: `${verticalTrackPosition(eventIndex, track)}px` }">
                <i></i><div><strong>{{ event.date }}</strong><em v-if="track.key !== 'international-ai'">【{{ trackTag(track.key) }}】</em><small>{{ event.title }}</small></div>
              </article>
            </template>

            <article class="portrait-event portrait-finale copy-right">
              <i></i><div><strong>{{ data.finale.date }}</strong><em>{{ isEnLang ? 'FINALE' : '总决赛' }}</em><small>{{ data.finale.title }} · {{ data.finale.place }}</small></div>
            </article>
          </div>
        </div>
      </transition>

      <p class="schedule-note">{{ data.subTitle }}</p>
    </div>
  </section>
</template>

<script>
import SubTitle from '@/components/SubTitle'
import { mapState } from 'vuex'

export default {
  props: ['data'],
  components: { SubTitle },
  data: () => ({ scheduleRegion: 'domestic' }),
  computed: {
    ...mapState(['isEnLang']),
    title () { return this.isEnLang ? 'Competition Schedule' : '大赛日程' },
    orderedTracks () {
      const order = ['domestic-ai', 'data', 'international-ai']
      return order.map(key => this.data.tracks.find(track => track.key === key)).filter(Boolean)
    },
    filteredTracks () {
      return this.orderedTracks.filter(track => this.scheduleRegion === 'international' ? track.key === 'international-ai' : track.key !== 'international-ai')
    },
    visibleCommonEvents () {
      return this.scheduleRegion === 'international' ? this.data.common.slice(0, 1) : this.data.common
    },
    regionTitle () {
      if (this.isEnLang) return this.scheduleRegion === 'domestic' ? 'Domestic Competition' : 'International Competition'
      return this.scheduleRegion === 'domestic' ? '国内赛道赛程' : '国际赛道赛程'
    },
    regionDescription () {
      if (this.isEnLang) return this.scheduleRegion === 'domestic' ? 'Artificial Intelligence and Data Analytics are presented separately.' : 'Official language: English.'
      return this.scheduleRegion === 'domestic' ? '人工智能赛道与数据分析赛道分别展示。' : '人工智能国际赛道，官方语言为英文。'
    }
  },
  methods: {
    commonPosition (index) {
      if (this.scheduleRegion === 'international') return 12
      return [8.5, 16, 23][index] || 23
    },
    trackPosition (index, track) {
      // Keep every track marker on the straight section of its route. The
      // final data-analysis milestone is intentionally pulled back from the
      // merge curve so the 11.22 marker reads as an independent checkpoint.
      const bounds = track.key === 'domestic-ai' ? [42, 83] : track.key === 'international-ai' ? [29, 80] : [37, 82]
      const count = track.events.length
      if (count <= 1) return (bounds[0] + bounds[1]) / 2
      return bounds[0] + (bounds[1] - bounds[0]) * index / (count - 1)
    },
    verticalTrackPosition (index, track) {
      const count = track.events.length
      if (count <= 1) return 560
      return 350 + 420 * index / (count - 1)
    },
    verticalCommonPosition (index) {
      const count = this.visibleCommonEvents.length
      // Keep shared milestones on the common vertical segment, with enough
      // breathing room for long labels on narrow screens.
      if (count <= 1) return 90
      return 62 + 160 * index / (count - 1)
    },
    trackTag (key) {
      if (this.isEnLang) return key === 'international-ai' ? 'INTERNATIONAL AI TRACK' : key === 'domestic-ai' ? 'AI TRACK' : 'DATA ANALYTICS TRACK'
      return key === 'data' ? '数据分析赛道' : '人工智能赛道'
    }
  }
}
</script>

<style lang="scss" scoped>
.schedule { position: relative; padding: 76px 0 82px; background: linear-gradient(180deg, #fff 0%, #fff 72%, #fafbfe 100%); overflow: hidden; }
.timezone { margin: -22px 0 24px; color: #9299a7; font-size: 11px; letter-spacing: .02em; text-align: center; }
.schedule-region-tabs { display: grid; grid-template-columns: repeat(2, 1fr); gap: 5px; width: 640px; max-width: 100%; margin: 0 auto 26px; padding: 5px; border: 1px solid rgba(35,55,102,.04); border-radius: 15px; background: #f1f3f8; }
.schedule-region-tabs button { display: flex; align-items: center; gap: 13px; min-height: 62px; padding: 10px 17px; border: 0; border-radius: 11px; color: #7b8391; text-align: left; background: transparent; cursor: pointer; transition: color .22s ease, background .22s ease, box-shadow .22s ease, transform .22s ease; }
.schedule-region-tabs button > span:first-child { color: #a2a8b2; font-family: DIN-BlackItalic, DIN; font-size: 14px; }
.schedule-region-tabs .tab-copy { display: flex; flex-direction: column; gap: 3px; }
.schedule-region-tabs strong { font-size: 15px; }
.schedule-region-tabs small { color: #9299a5; font-size: 10px; }
.schedule-region-tabs button.active { color: #17213a; background: #fff; box-shadow: 0 5px 16px rgba(30,47,92,.08); transform: translateY(-1px); }
.schedule-region-tabs button.active > span:first-child { color: #3156dc; }
.schedule-region-tabs button:focus-visible { outline: 3px solid rgba(49,86,220,.22); outline-offset: 2px; }
.region-panel { position: relative; margin-top: 16px; padding: 25px 32px 16px; border: 1px solid rgba(38,65,126,.09); border-radius: 20px; background: linear-gradient(145deg, #fff 0%, #fbfcff 58%, #f8f9ff 100%); box-shadow: 0 16px 46px rgba(36,55,101,.065); overflow: hidden; }
.region-panel--international { --international: #079db9; background: linear-gradient(145deg, #fff 0%, #f8fcff 58%, #f2fbfc 100%); }
.region-panel--international .region-heading > span { color: #078da9; }
.region-panel::after { content: ''; position: absolute; right: -180px; top: -220px; width: 420px; height: 420px; border: 1px solid rgba(49,86,220,.08); border-radius: 50%; box-shadow: 0 0 0 34px rgba(237,49,95,.025), 0 0 0 68px rgba(116,75,218,.025); pointer-events: none; }
.region-heading { position: relative; z-index: 1; display: grid; grid-template-columns: 118px 1fr; align-items: end; column-gap: 20px; padding-bottom: 16px; border-bottom: 1px solid rgba(23,33,58,.085); }
.region-heading > span { grid-row: 1 / 3; align-self: center; color: #3156dc; font-family: DIN-BlackItalic, DIN; font-size: 12px; letter-spacing: .1em; }
.region-heading h3 { color: #17213a; font-size: 24px; line-height: 1.3; letter-spacing: -.02em; }
.region-heading p { margin-top: 3px; color: #7d8697; font-size: 11px; }
.workshop-notice { position: relative; z-index: 2; display: flex; align-items: center; gap: 12px; width: fit-content; margin: 18px 0 0 138px; padding: 9px 15px; border-left: 3px solid #7457ce; color: #677187; background: rgba(116,87,206,.055); }
.workshop-notice span { color: #7457ce; font-family: DIN-BlackItalic, DIN; font-size: 12px; font-weight: 700; }
.workshop-notice strong { color: #35415a; font-size: 11px; font-weight: 600; }
.horizontal-timeline { --common: #8457c7; --ai: #3f62dc; --data: #e13e72; position: relative; z-index: 1; min-width: 1080px; height: 430px; }
.horizontal-routes { position: absolute; inset: 0; width: 100%; height: 100%; overflow: visible; }
.horizontal-routes path { fill: none; stroke-width: 6; stroke-linecap: round; stroke-linejoin: round; vector-effect: non-scaling-stroke; filter: drop-shadow(0 3px 4px rgba(49,86,220,.08)); }
.horizontal-routes .route-common { stroke: #9b55ba; }
.horizontal-routes .route-ai { stroke: url(#horizontalAi); }
.horizontal-routes .route-data { stroke: url(#horizontalData); }
.horizontal-routes .route-international { stroke: url(#horizontalInternational); }
.wide-event { position: absolute; z-index: 2; width: 126px; text-align: center; transform: translateX(-50%); }
.wide-event i { position: absolute; z-index: 1; left: 50%; width: 20px; height: 20px; border: 5px solid #fff; border-radius: 50%; background: var(--common); box-shadow: 0 0 0 1px rgba(132,87,199,.2), 0 5px 12px rgba(56,70,113,.13); transform: translateX(-50%); }
.wide-event strong { display: block; color: #17213a; font-family: DIN-BlackItalic, DIN; font-size: 19px; line-height: 1.12; }
.wide-event small { display: block; margin-top: 7px; color: #46546c; font-size: 11px; line-height: 1.45; }
.wide-event em { display: block; margin-top: 6px; color: var(--ai); font-size: 9px; font-style: normal; font-weight: 700; }
.wide-event::before { content: ''; position: absolute; left: 50%; width: 1px; background: rgba(132,87,199,.38); }
.wide-common.above { top: 112px; }
.wide-common.below { top: 258px; }
.wide-common.above i { top: 94px; }
.wide-common.above::before { top: 54px; height: 48px; }
.wide-common.below i { top: -53px; }
.wide-common.below::before { top: -44px; height: 46px; }
.wide-track.lane-0 { top: 44px; }
.wide-track.lane-1 { top: 330px; }
.wide-track.lane-0 i { top: 77px; background: var(--ai); }
.wide-track.lane-0::before { top: 54px; height: 31px; background: rgba(63,98,220,.38); }
.wide-track.lane-1 i { top: -41px; background: var(--data); }
.wide-track.lane-1::before { top: -32px; height: 34px; background: rgba(225,62,114,.38); }
.wide-data em { color: var(--data); }
.wide-data { width: 108px; }
.wide-international-ai { width: 132px; }
.wide-international-ai small { font-size: 10px; line-height: 1.35; }
.horizontal-timeline.is-single .wide-track.lane-0 { top: 128px; }
.horizontal-timeline.is-single .wide-track.lane-0 i { top: 77px; background: var(--common); }
.region-panel--international .horizontal-timeline.is-single .wide-track.lane-0 i { background: #168dc5; box-shadow: 0 0 0 2px rgba(7,157,185,.17), 0 6px 14px rgba(56,70,113,.14); }
.region-panel--international .horizontal-timeline.is-single .wide-track.lane-0 em { color: #078da9; }
.wide-finale { right: 0; top: 258px; transform: none; }
.wide-finale i { top: -53px; background: var(--ai); }
.wide-finale::before { top: -44px; height: 46px; background: rgba(63,98,220,.38); }
.wide-finale em { color: var(--ai); }
.vertical-timeline { --common: #8457c7; --ai: #3f62dc; --data: #e13e72; position: relative; z-index: 1; display: none; width: min(700px, 100%); height: 960px; margin: 12px auto 0; }
.vertical-routes { position: absolute; inset: 0; width: 100%; height: 100%; overflow: visible; }
.vertical-routes path { fill: none; stroke-width: 7; stroke-linecap: round; stroke-linejoin: round; vector-effect: non-scaling-stroke; filter: drop-shadow(0 3px 4px rgba(49,86,220,.08)); }
.vertical-routes .route-common { stroke: url(#verticalCommon); }
.vertical-routes .route-ai { stroke: url(#verticalAi); }
.vertical-routes .route-data { stroke: url(#verticalData); }
.vertical-routes .route-international { stroke: url(#verticalInternational); }
.vertical-routes .route-final { stroke: url(#verticalFinal); }
.portrait-event { position: absolute; z-index: 2; left: 50%; width: 100%; transform: translateX(-50%); }
.portrait-event > i { position: absolute; left: 50%; top: 0; width: 22px; height: 22px; border: 5px solid #fff; border-radius: 50%; background: var(--common); box-shadow: 0 0 0 1px rgba(132,87,199,.2), 0 6px 14px rgba(56,70,113,.14); transform: translateX(-50%); }
.portrait-event > div { position: absolute; top: -2px; width: calc(50% - 44px); max-width: calc(50% - 44px); box-sizing: border-box; }
.portrait-event.copy-left > div { right: calc(50% + 38px); text-align: right; }
.portrait-event.copy-right > div { left: calc(50% + 38px); text-align: left; }
.portrait-event > div::before { content: ''; position: absolute; top: 13px; width: 22px; height: 1px; background: rgba(132,87,199,.55); }
.portrait-event.copy-left > div::before { right: -30px; }
.portrait-event.copy-right > div::before { left: -30px; }
.portrait-event strong { display: block; color: #17213a; font-family: DIN-BlackItalic, DIN; font-size: 19px; line-height: 1.15; }
.portrait-event small { display: block; margin-top: 6px; color: #59657a; font-size: 10px; line-height: 1.5; }
.portrait-event em { display: block; margin-top: 5px; color: var(--ai); font-size: 9px; font-style: normal; font-weight: 700; }
.portrait-event strong, .portrait-event small, .portrait-event em { overflow-wrap: anywhere; word-break: break-word; }
.portrait-track.portrait-lane-0 { left: 34%; }
.portrait-track.portrait-lane-1 { left: 66%; }
.portrait-track.portrait-lane-0 > i { background: var(--ai); }
.portrait-track.portrait-lane-1 > i { background: var(--data); }
.portrait-track.portrait-lane-0 > div::before { background: rgba(63,98,220,.55); }
.portrait-track.portrait-lane-1 > div::before { background: rgba(225,62,114,.55); }
.portrait-track.portrait-lane-1 em { color: var(--data); }
.vertical-timeline.is-single .portrait-track.portrait-lane-0 { left: 50%; }
.region-panel--international .portrait-track.portrait-lane-0 > i { background: #168dc5; box-shadow: 0 0 0 2px rgba(7,157,185,.17), 0 7px 16px rgba(56,70,113,.15); }
.region-panel--international .portrait-track.portrait-lane-0 > div::before { background: rgba(7,157,185,.55); }
.region-panel--international .portrait-track.portrait-lane-0 em { color: #078da9; }
.portrait-finale { top: 920px; }
.portrait-finale > i { background: var(--ai); }
.schedule-note { max-width: 920px; margin: 22px auto 0; color: #969dab; font-size: 10px; line-height: 1.75; text-align: center; }
.schedule-panel-enter-active, .schedule-panel-leave-active { transition: opacity .28s cubic-bezier(.2,.75,.25,1), transform .28s cubic-bezier(.2,.75,.25,1); }
.schedule-panel-enter { opacity: 0; transform: translateX(18px); }
.schedule-panel-leave-to { opacity: 0; transform: translateX(-12px); }

@include mobile {
  .schedule { padding: 58px 0; }
  .timezone { margin: -8px 0 22px; text-align: left; }
  .schedule-region-tabs { gap: 4px; }
  .schedule-region-tabs button { min-height: 58px; padding: 9px 10px; }
  .schedule-region-tabs button > span:first-child { display: none; }
  .schedule-region-tabs strong { font-size: 13px; }
  .schedule-region-tabs small { font-size: 8px; }
  .region-panel { padding: 22px 16px; border-radius: 14px; }
  .region-heading { display: block; }
  .region-heading > span { display: block; margin-bottom: 8px; }
  .region-heading h3 { font-size: 21px; }
  .workshop-notice { margin: 15px 0 0; }
  .schedule-note { text-align: left; }
}

@media (max-width: 1240px) {
  .horizontal-timeline { display: none; }
  .vertical-timeline { display: block; }
}

@media (max-width: 600px) {
  .schedule-region-tabs { margin-bottom: 20px; }
  .region-panel { padding: 20px 14px 14px; }
  .workshop-notice { width: 100%; align-items: flex-start; flex-direction: column; gap: 3px; padding: 9px 11px; }
  .vertical-timeline { width: calc(100% - 8px); margin-top: 6px; }
  /* Keep the event wrapper from extending beyond the narrow panel.  The
     marker still sits on its lane, while the copy gains a safe inner gutter
     so dates and track labels are never clipped on compact phones. */
  .portrait-event { width: 72%; }
  .portrait-event > div { width: calc(50% - 32px); max-width: calc(50% - 32px); }
  .portrait-event.copy-left > div { right: calc(50% + 28px); }
  .portrait-event.copy-right > div { left: calc(50% + 28px); }
  .portrait-event > div::before { width: 12px; }
  .portrait-event.copy-left > div::before { right: -18px; }
  .portrait-event.copy-right > div::before { left: -18px; }
  .portrait-event strong { font-size: 15px; }
  .portrait-event small { font-size: 9px; line-height: 1.45; }
  .portrait-event em { font-size: 8px; line-height: 1.35; }
  .portrait-track-label { font-size: 9px; }
}
</style>
